import { createBrowserRouter, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { lazy, Suspense } from 'react';

const LoginPage = lazy(() => import('@/modules/auth/LoginPage'));
const CompanyPickerPage = lazy(() => import('@/modules/companies/CompanyPickerPage'));
const Shell = lazy(() => import('@/components/layout/Shell'));
const DashboardPage = lazy(() => import('@/modules/dashboard/DashboardPage'));
const NotFoundPage = lazy(() => import('@/modules/NotFoundPage'));

// Contacts module
const ContactsPage = lazy(() => import('@/modules/contacts/ContactsPage'));
const ContactForm = lazy(() => import('@/modules/contacts/ContactForm'));
const ContactDetail = lazy(() => import('@/modules/contacts/ContactDetail'));

// Inventory module
const ProductsPage = lazy(() => import('@/modules/inventory/ProductsPage'));
const ProductForm = lazy(() => import('@/modules/inventory/ProductForm'));
const WarehousesPage = lazy(() => import('@/modules/inventory/WarehousesPage'));

// Sales module (Sprint 4)
const InvoiceListPage = lazy(() =>
  import('@/modules/sales/InvoiceListPage').then((m) => ({ default: m.InvoiceListPage }))
);
const InvoiceForm = lazy(() =>
  import('@/modules/sales/InvoiceForm').then((m) => ({ default: m.InvoiceForm }))
);
const InvoiceDetailPage = lazy(() =>
  import('@/modules/sales/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage }))
);

// Purchasing module (Sprint 6)
const PurchaseOrdersPage = lazy(() =>
  import('@/modules/purchasing/PurchaseOrdersPage').then((m) => ({ default: m.PurchaseOrdersPage }))
);
const PurchaseOrderForm = lazy(() =>
  import('@/modules/purchasing/PurchaseOrderForm').then((m) => ({ default: m.PurchaseOrderForm }))
);
const PurchaseOrderDetailPage = lazy(() =>
  import('@/modules/purchasing/PurchaseOrderDetailPage').then((m) => ({ default: m.PurchaseOrderDetailPage }))
);
const SupplierInvoicesPage = lazy(() =>
  import('@/modules/purchasing/SupplierInvoicesPage').then((m) => ({ default: m.SupplierInvoicesPage }))
);
const SupplierInvoiceForm = lazy(() =>
  import('@/modules/purchasing/SupplierInvoiceForm').then((m) => ({ default: m.SupplierInvoiceForm }))
);
const SupplierInvoiceDetailPage = lazy(() =>
  import('@/modules/purchasing/SupplierInvoiceDetailPage').then((m) => ({ default: m.SupplierInvoiceDetailPage }))
);
const ReceiptsPage = lazy(() =>
  import('@/modules/purchasing/ReceiptsPage').then((m) => ({ default: m.ReceiptsPage }))
);

// Inventory Sprint 7 additions
const StockPage = lazy(() =>
  import('@/modules/inventory/StockPage').then((m) => ({ default: m.StockPage }))
);
const MovementsPage = lazy(() =>
  import('@/modules/inventory/MovementsPage').then((m) => ({ default: m.MovementsPage }))
);
const TransfersPage = lazy(() =>
  import('@/modules/inventory/TransfersPage').then((m) => ({ default: m.TransfersPage }))
);

// Accounts Receivable/Payable module (Sprint 5)
const ARDashboard = lazy(() =>
  import('@/modules/accounts-receivable/ARDashboard').then((m) => ({ default: m.ARDashboard }))
);
const ARStatement = lazy(() =>
  import('@/modules/accounts-receivable/ARStatement').then((m) => ({ default: m.ARStatement }))
);
const APDashboard = lazy(() =>
  import('@/modules/accounts-receivable/APDashboard').then((m) => ({ default: m.APDashboard }))
);
const APStatement = lazy(() =>
  import('@/modules/accounts-receivable/APStatement').then((m) => ({ default: m.APStatement }))
);

// Treasury module (Sprint 8)
const TreasurySummaryPage = lazy(() => import('@/modules/treasury/TreasurySummaryPage'));
const CashAccountsPage = lazy(() => import('@/modules/treasury/CashAccountsPage'));
const BankAccountsPage = lazy(() => import('@/modules/treasury/BankAccountsPage'));
const TreasuryMovementsPage = lazy(() => import('@/modules/treasury/MovementsPage'));
const TreasuryReceiptForm = lazy(() => import('@/modules/treasury/ReceiptForm'));
const TreasuryPaymentForm = lazy(() => import('@/modules/treasury/PaymentForm'));
const TreasuryTransferForm = lazy(() => import('@/modules/treasury/TransferForm'));
const ChecksPage = lazy(() => import('@/modules/treasury/ChecksPage'));
const ExchangeRatesPage = lazy(() => import('@/modules/treasury/ExchangeRatesPage'));
const CashMovementForm = lazy(() => import('@/modules/treasury/CashMovementForm'));

// Accounting module (Sprint 9)
const AccountsTreePage = lazy(() => import('@/modules/accounting/AccountsTreePage'));
const FiscalYearsPage = lazy(() => import('@/modules/accounting/FiscalYearsPage'));
const JournalEntriesPage = lazy(() => import('@/modules/accounting/JournalEntriesPage'));
const JournalEntryForm = lazy(() => import('@/modules/accounting/JournalEntryForm'));
const JournalEntryDetailPage = lazy(() => import('@/modules/accounting/JournalEntryDetailPage'));
const TrialBalancePage = lazy(() => import('@/modules/accounting/reports/TrialBalancePage'));
const BalanceSheetPage = lazy(() => import('@/modules/accounting/reports/BalanceSheetPage'));
const IncomeStatementPage = lazy(() => import('@/modules/accounting/reports/IncomeStatementPage'));
const GeneralLedgerPage = lazy(() => import('@/modules/accounting/reports/GeneralLedgerPage'));

// Reports module (Sprint 10)
const ReportsPage = lazy(() => import('@/modules/reports/ReportsPage'));
const SalesReportPage = lazy(() => import('@/modules/reports/SalesReportPage'));
const PurchasesReportPage = lazy(() => import('@/modules/reports/PurchasesReportPage'));
const ARAgingPage = lazy(() => import('@/modules/reports/ARAgingPage'));
const APAgingPage = lazy(() => import('@/modules/reports/APAgingPage'));
const InventoryReportPage = lazy(() => import('@/modules/reports/InventoryReportPage'));
const CashflowPage = lazy(() => import('@/modules/reports/CashflowPage'));

// AI module (Sprint 11)
const DocumentProcessingPage = lazy(() => import('@/modules/ai-assistant/DocumentProcessingPage'));

// Settings
const SettingsPage = lazy(() => import('@/modules/settings/SettingsPage'));
const DiscountGroupsPage = lazy(() => import('@/modules/settings/DiscountGroupsPage'));

// Help
const HelpPage = lazy(() => import('@/modules/help/HelpPage'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireCompany({ children }: { children: React.ReactNode }) {
  const selectedCompany = useAuthStore((s) => s.selectedCompany);
  if (!selectedCompany) return <Navigate to="/companies" replace />;
  return <>{children}</>;
}

function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <S><LoginPage /></S>,
  },
  {
    path: '/companies',
    element: <RequireAuth><S><CompanyPickerPage /></S></RequireAuth>,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <RequireCompany>
          <S><Shell /></S>
        </RequireCompany>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        element: <S><DashboardPage /></S>,
      },

      // ---- Contacts ----
      { path: 'contacts', element: <S><ContactsPage /></S> },
      { path: 'contacts/new', element: <S><ContactForm /></S> },
      { path: 'contacts/:id', element: <S><ContactDetail /></S> },
      { path: 'contacts/:id/edit', element: <S><ContactForm /></S> },

      // ---- Inventory ----
      { path: 'inventory', element: <Navigate to="/inventory/products" replace /> },
      { path: 'inventory/products', element: <S><ProductsPage /></S> },
      { path: 'inventory/products/new', element: <S><ProductForm /></S> },
      { path: 'inventory/products/:id', element: <S><ProductsPage /></S> },
      { path: 'inventory/products/:id/edit', element: <S><ProductForm /></S> },
      { path: 'inventory/warehouses', element: <S><WarehousesPage /></S> },
      // Sprint 7
      { path: 'inventory/stock', element: <S><StockPage /></S> },
      { path: 'inventory/movements', element: <S><MovementsPage /></S> },
      { path: 'inventory/transfers', element: <S><TransfersPage /></S> },

      // ---- Purchasing (Sprint 6) ----
      { path: 'purchasing', element: <Navigate to="/purchasing/orders" replace /> },
      { path: 'purchasing/orders', element: <S><PurchaseOrdersPage /></S> },
      { path: 'purchasing/orders/new', element: <S><PurchaseOrderForm /></S> },
      { path: 'purchasing/orders/:id', element: <S><PurchaseOrderDetailPage /></S> },
      { path: 'purchasing/supplier-invoices', element: <S><SupplierInvoicesPage /></S> },
      { path: 'purchasing/supplier-invoices/new', element: <S><SupplierInvoiceForm /></S> },
      { path: 'purchasing/supplier-invoices/:id', element: <S><SupplierInvoiceDetailPage /></S> },
      { path: 'purchasing/receipts', element: <S><ReceiptsPage /></S> },

      // ---- Sales (Sprint 4) ----
      { path: 'sales', element: <Navigate to="/sales/documents" replace /> },
      { path: 'sales/documents', element: <S><InvoiceListPage /></S> },
      { path: 'sales/documents/:id', element: <S><InvoiceDetailPage /></S> },
      { path: 'sales/invoices/new', element: <S><InvoiceForm /></S> },
      { path: 'sales/quotations/new', element: <S><InvoiceForm /></S> },
      { path: 'sales/credit-notes/new', element: <S><InvoiceForm /></S> },
      { path: 'sales/debit-notes/new', element: <S><InvoiceForm /></S> },

      // ---- AR — Accounts Receivable (Sprint 5) ----
      { path: 'ar', element: <S><ARDashboard /></S> },
      { path: 'ar/list', element: <S><ARDashboard /></S> },
      { path: 'ar/statement/:contactId', element: <S><ARStatement /></S> },

      // ---- AP — Accounts Payable (Sprint 5) ----
      { path: 'ap', element: <S><APDashboard /></S> },
      { path: 'ap/statement/:contactId', element: <S><APStatement /></S> },

      // ---- Treasury (Sprint 8) ----
      { path: 'treasury', element: <S><TreasurySummaryPage /></S> },
      { path: 'treasury/cash-accounts', element: <S><CashAccountsPage /></S> },
      { path: 'treasury/bank-accounts', element: <S><BankAccountsPage /></S> },
      { path: 'treasury/movements', element: <S><TreasuryMovementsPage /></S> },
      { path: 'treasury/receipts/new', element: <S><TreasuryReceiptForm /></S> },
      { path: 'treasury/payments/new', element: <S><TreasuryPaymentForm /></S> },
      { path: 'treasury/transfers/new', element: <S><TreasuryTransferForm /></S> },
      { path: 'treasury/cash-movements/new', element: <S><CashMovementForm /></S> },
      { path: 'treasury/checks', element: <S><ChecksPage /></S> },
      { path: 'treasury/exchange-rates', element: <S><ExchangeRatesPage /></S> },

      // ---- Accounting (Sprint 9) ----
      { path: 'accounting', element: <Navigate to="/accounting/accounts" replace /> },
      { path: 'accounting/accounts', element: <S><AccountsTreePage /></S> },
      { path: 'accounting/fiscal-years', element: <S><FiscalYearsPage /></S> },
      { path: 'accounting/entries', element: <S><JournalEntriesPage /></S> },
      { path: 'accounting/entries/new', element: <S><JournalEntryForm /></S> },
      { path: 'accounting/entries/:id', element: <S><JournalEntryDetailPage /></S> },
      { path: 'accounting/reports/trial-balance', element: <S><TrialBalancePage /></S> },
      { path: 'accounting/reports/balance-sheet', element: <S><BalanceSheetPage /></S> },
      { path: 'accounting/reports/income-statement', element: <S><IncomeStatementPage /></S> },
      { path: 'accounting/reports/general-ledger', element: <S><GeneralLedgerPage /></S> },

      // ---- Reports (Sprint 10) ----
      { path: 'reports', element: <S><ReportsPage /></S> },
      { path: 'reports/sales', element: <S><SalesReportPage /></S> },
      { path: 'reports/purchases', element: <S><PurchasesReportPage /></S> },
      { path: 'reports/ar-aging', element: <S><ARAgingPage /></S> },
      { path: 'reports/ap-aging', element: <S><APAgingPage /></S> },
      { path: 'reports/inventory', element: <S><InventoryReportPage /></S> },
      { path: 'reports/cashflow', element: <S><CashflowPage /></S> },

      // ---- AI Assistant (Sprint 11) ----
      { path: 'ai/documents', element: <S><DocumentProcessingPage /></S> },

      // ---- Settings ----
      { path: 'settings', element: <S><SettingsPage /></S> },
      { path: 'settings/discount-groups', element: <S><DiscountGroupsPage /></S> },

      // ---- Help ----
      { path: 'help', element: <S><HelpPage /></S> },
    ],
  },
  {
    path: '*',
    element: <S><NotFoundPage /></S>,
  },
]);
